import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Zap } from "lucide-react";
import { MaskLines, ease, useIsMobile } from "./motion";
import { Orbit } from "./Orbit";
import { Starfield } from "./Starfield";
import { scrollToId } from "@/lib/scroll";

const useTelemetry = () => {
  const [t, setT] = useState({ ms: 1.4, mbps: 842 });
  useEffect(() => {
    const id = setInterval(() => setT({ ms: +(1.1 + Math.random() * 0.8).toFixed(1), mbps: Math.round(780 + Math.random() * 160) }), 1200);
    return () => clearInterval(id);
  }, []);
  return t;
};

const fade = (ready, delay) => ({ initial: { opacity: 0, y: 20 }, animate: ready ? { opacity: 1, y: 0 } : {}, transition: { duration: 1, ease, delay } });

export const Hero = ({ ready }) => {
  const [failover, setFailover] = useState(false);
  const ref = useRef(null);
  const t = useTelemetry();
  const mobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] overflow-hidden pt-28 pb-10" data-testid="hero-section">
      <div className="absolute inset-0 grid-bg" aria-hidden />
      <Starfield />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center min-h-[calc(100svh-10rem)]">
        <motion.div style={{ y: yText, opacity: fadeOut }} className="relative z-10">
          <motion.p {...fade(ready, 0.2)} className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400 mb-8 flex items-center gap-3">
            <span className="text-cyan-300 whitespace-nowrap">[ LNS ]</span> Network engineering<br className="sm:hidden" /> for business
          </motion.p>
          <h1 className="font-display font-extrabold tracking-[-0.035em] leading-[0.92] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.6rem]" data-testid="hero-headline" aria-label="Networks that just work, cable to cloud.">
            <MaskLines
              play={ready}
              delay={0.15}
              lines={[
                "Networks that",
                <>
                  <span className="font-serif-i font-normal text-cyan-300 tracking-normal">just work</span>
                  <span className="text-slate-500">,</span>
                </>,
                "cable to cloud.",
              ]}
            />
          </h1>
          <motion.p {...fade(ready, 0.75)} className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed">
            Design, installation and support for business networks built to enterprise standards — Ubiquiti UniFi for small business, Cisco, Palo Alto, Fortinet and Azure for the bigger estates. One engineer. One dashboard.
          </motion.p>
          <motion.div {...fade(ready, 0.9)} className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
            <button onClick={() => scrollToId("contact")} data-testid="hero-primary-cta" className="group inline-flex items-center gap-3 rounded-full bg-cyan-300 text-void pl-7 pr-2 py-2 font-semibold hover:bg-white transition-colors">
              Book a free consultation
              <span className="grid h-10 w-10 place-items-center rounded-full bg-void text-cyan-300 transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
            </button>
            <button onClick={() => setFailover((f) => !f)} data-testid="hero-failover-toggle" className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-3.5 sm:px-6 sm:py-4 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${failover ? "border-rose-400/60 text-rose-300 bg-rose-500/10" : "border-white/15 text-slate-300 hover:border-cyan-300/60 hover:text-cyan-200"}`}>
              <Zap size={14} /> {failover ? "Restore WAN 1" : "Simulate failover"}
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={mobile ? undefined : { y: yVisual }} className="relative -mx-2 mt-4 sm:mx-0 lg:mt-0">
          <Orbit ready={ready} failover={failover} compact={mobile} />
          <motion.div {...fade(ready, 1.4)} className="mt-2 grid grid-cols-3 gap-2 px-2 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500 sm:px-0 lg:absolute lg:-bottom-10 lg:left-0 lg:right-0 lg:mt-0 lg:flex lg:justify-between lg:text-[10px] lg:tracking-[0.2em]" data-testid="hero-telemetry">
            <span>LAT <span className="text-cyan-300 tabular-nums">{t.ms}ms</span></span>
            <span className="text-center lg:text-left">THRU <span className="text-cyan-300 tabular-nums">{t.mbps} Mb/s</span></span>
            <span data-testid="hero-failover-status" className={`text-right lg:text-left ${failover ? "text-emerald-400" : ""}`}>{failover ? (mobile ? "REROUTED · 0.8s" : "REROUTED → STARLINK · 0.8s") : "WAN 1 · PRIMARY"}</span>
          </motion.div>
        </motion.div>
      </div>
      <motion.div {...fade(ready, 1.6)} className="relative mx-auto max-w-[1400px] px-5 sm:px-8 mt-10 sm:mt-12 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
        <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] text-slate-500 leading-relaxed">Cabling · Wi-Fi · Firewalls<span className="hidden sm:inline"> · </span><br className="sm:hidden" />SD-WAN · CCTV · Access</span>
        <button onClick={() => scrollToId("manifesto")} data-testid="hero-scroll-cue" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:text-white">
          Scroll <ArrowDownRight size={14} className="animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};
